import type { DTO } from '~/core/types'

import type {
  Cart,
  CartSoldProduct,
  InformationsReservationDTO,
  Pay3dSecureDTO,
  PayDTO,
  PostBookingsDTO,
  Recap,
} from '../types/order'
import type { BookingLite } from '../types/shared'

export type BeneficiaryDTO = DTO<{ beneficiary_is_user: boolean, beneficiary_first_name: string, beneficiary_last_name: string, beneficiary_email: string, beneficiary_phone: string }>

export const useOrderService = () => {
  const config = useRuntimeConfig()
  const BASE_URL = `${config.public.apiBase}/api/order`

  return {
    async getCart() {
      const data = await fetchJson<FetchResult<Cart | []>>(
        'GET', `${BASE_URL}`,
      )
      return Array.isArray(data?.data) ? null : data as FetchResult<Cart>
    },
    patchAdditionalProduct(dto: DTO<{ booking_id: number, additional_product_id: number }>) {
      return fetchJson<FetchResult<void>, typeof dto>(
        'PATCH', `${BASE_URL}/additional-product`, dto,
      )
    },
    deleteAdditionalProduct(dto: { booking_id: number, additional_product_id: number, all: boolean }) {
      return fetchJson<FetchResult<void>, typeof dto>(
        'DELETE', `${BASE_URL}/additional-product`, dto,
      )
    },
    postBookings(dto: PostBookingsDTO) {
      return fetchJson<FetchResult<Cart>, typeof dto>(
        'POST', `${BASE_URL}/bookings`, dto,
      )
    },
    getBooking(id: number) {
      return fetchJson<FetchResult<BookingLite>>(
        'GET', `${BASE_URL}/bookings/${id}`,
      )
    },
    deleteBooking(id: number) {
      return fetchJson<FetchResult<Cart>>(
        'DELETE', `${BASE_URL}/bookings/${id}`,
      )
    },
    postBookingCancel(bookingId: number) {
      return fetchJson(
        'POST', `${BASE_URL}/bookings/${bookingId}/cancel`,
      )
    },
    patchBookingPlayerBeGolf(bookingId: number, playerId: number, dto: { email: string }) {
      return fetchJson<FetchResult<Cart>, typeof dto>(
        'PATCH', `${BASE_URL}/bookings/${bookingId}/players/${playerId}/be-golf`, dto,
      )
    },
    patchBookingPlayerCancelProcing(bookingId: number, playerId: number) {
      return fetchJson<FetchResult<Cart>>(
        'PATCH', `${BASE_URL}/bookings/${bookingId}/players/${playerId}/cancel-pricing`,
      )
    },
    patchBookingPlayerCoupon(bookingId: number, playerId: number, dto: { code: string }) {
      return fetchJson<FetchResult<Cart>, typeof dto>(
        'PATCH', `${BASE_URL}/bookings/${bookingId}/players/${playerId}/coupon`, dto,
      )
    },
    patchBookingPlayerLeClubGolf(bookingId: number, playerId: number, dto: { number: string }) {
      return fetchJson<FetchResult<Cart>, typeof dto>(
        'PATCH', `${BASE_URL}/bookings/${bookingId}/players/${playerId}/le-club-golf`, dto,
      )
    },
    patchBookingPlayerSpecialPrice(bookingId: number, playerId: number, dto: { special_price_id: number }) {
      return fetchJson<FetchResult<Cart>, typeof dto>(
        'PATCH', `${BASE_URL}/bookings/${bookingId}/players/${playerId}/special-price`, dto,
      )
    },
    patchInformationsReservation(dto: InformationsReservationDTO) {
      return fetchJson<FetchResult<void>, typeof dto>(
        'PATCH', `${BASE_URL}/informations-reservation`, dto,
      )
    },
    postPay(dto: PayDTO) {
      return fetchJson<FetchResult<Recap>, typeof dto>(
        'POST', `${BASE_URL}/pay`, dto,
      )
    },
    postPay3dSecure(dto: Pay3dSecureDTO) {
      return fetchJson<FetchResult<Recap>, typeof dto>(
        'POST', `${BASE_URL}/pay-3d-secure`, dto,
      )
    },
    postProduct(productId: number, dto: { golf_id: number, booking_place: string }) {
      return fetchJson<FetchResult<Cart>, typeof dto>(
        'POST', `${BASE_URL}/products/${productId}`, dto,
      )
    },
    applyVoucherToProducts(code: string) {
      return fetchJson<FetchResult<Cart>, { code: string }>(
        'POST', `${BASE_URL}/products/coupon`, { code },
      )
    },
    deleteSoldProduct(soldProductId: number) {
      return fetchJson<FetchResult<Cart>>(
        'DELETE', `${BASE_URL}/sold-products/${soldProductId}`,
      )
    },
    patchSoldProductBeneficiary(soldProductId: number, dto: BeneficiaryDTO) {
      return fetchJson<FetchResult<CartSoldProduct>, typeof dto>(
        'PATCH', `${BASE_URL}/sold-products/${soldProductId}/beneficiary`, dto,
      )
    },
  }
}
